import React, { useEffect, useMemo, useState } from 'react';
import TopBar from '../../components/layout/TopBar';
import Footer from '../../components/layout/Footer';
import { useBookingForm } from '../../hooks/useBookingForm';
import useBookingDataModel from '../../hooks/useBookingDataModel';
import useBookingAvailability from '../../hooks/useBookingAvailability';
import useBookingSubmission from '../../hooks/useBookingSubmission';
import { SINGLE_STEP_BOOKING_FLOW } from '../../constants/bookingConstants';
import { resolveServicePricingConfig } from '../../config/services/servicePricingRules';
import {
  BookingBreadcrumb,
  BookingFormNavigation,
  BookingHeader,
  BookingOrderSummary
} from './BookingPageSections';
import {
  BookingStepEvent,
  BookingStepPersonal
} from './steps';

const BookingPage = ({ onNavigate, bookingData, servicePage = 'wedding-services' }) => {
  const prefilledFormData = useMemo(() => {
    const customerName = String(bookingData?.customerInfo?.name || '').trim();
    const [firstName = '', ...lastNameParts] = customerName.split(' ').filter(Boolean);
    const derivedLastName = lastNameParts.join(' ');

    return {
      firstName,
      lastName: derivedLastName,
      email: String(bookingData?.customerInfo?.email || ''),
      phone: String(bookingData?.customerInfo?.phone || ''),
      address: String(bookingData?.customerInfo?.address || ''),
      eventDate: String(bookingData?.preBooking?.date || bookingData?.date || ''),
      eventTime: String(bookingData?.preBooking?.time || bookingData?.time || ''),
      specialRequirements: String(bookingData?.preBooking?.specialRequest || bookingData?.specialRequest || '')
    };
  }, [bookingData]);

  const {
    currentStep,
    formData,
    errors,
    handleInputChange,
    validateCurrentStep,
    totalSteps,
    flow
  } = useBookingForm({
    flow: SINGLE_STEP_BOOKING_FLOW,
    initialData: prefilledFormData
  });

  const {
    booking,
    serviceInfo,
    packageInfo,
    addOnItems,
    totalAmount,
    pricingBreakdown,
    breadcrumb,
    bookingMode,
    selectedPanditName
  } = useBookingDataModel(bookingData, servicePage);

  const [editablePricing, setEditablePricing] = useState(pricingBreakdown);

  useEffect(() => {
    setEditablePricing(pricingBreakdown);
  }, [pricingBreakdown]);

  const servicePricingConfig = useMemo(() => resolveServicePricingConfig(serviceInfo), [serviceInfo]);
  const hasDakshinaPricing = Boolean(servicePricingConfig?.dakshina);

  const effectivePricingBreakdown = useMemo(() => {
    const basePricing = editablePricing || pricingBreakdown;
    const dakshina = Number(basePricing?.dakshina || 0);
    const platformFee = Number(basePricing?.platformFee || 0);
    const addOns = Number(basePricing?.addOns || 0);

    return {
      ...pricingBreakdown,
      ...basePricing,
      dakshina,
      platformFee,
      addOns,
      total: dakshina + platformFee + addOns
    };
  }, [editablePricing, pricingBreakdown]);

  const effectiveTotalAmount = Number(effectivePricingBreakdown?.total || totalAmount || 0);

  const bookingForSubmission = useMemo(() => {
    return {
      ...booking,
      totalPrice: effectiveTotalAmount,
      pricing: {
        ...(booking?.pricing || {}),
        ...effectivePricingBreakdown,
        total: effectiveTotalAmount
      }
    };
  }, [booking, effectivePricingBreakdown, effectiveTotalAmount]);

  const handleDakshinaChange = (amount) => {
    const numericAmount = Number(amount || 0);
    setEditablePricing((prev) => {
      const nextPricing = prev || pricingBreakdown;
      const platformFee = Number(nextPricing?.platformFee || 0);
      const addOns = Number(nextPricing?.addOns || 0);
      return {
        ...nextPricing,
        dakshina: numericAmount,
        total: numericAmount + platformFee + addOns
      };
    });
  };

  const searchRole = useMemo(() => {
    return serviceInfo?.actorRole === 'service_provider' ? 'service_provider' : 'pandit';
  }, [serviceInfo]);

  const finalStepNumber = 'single';

  const {
    availabilityWindow,
    availabilityState,
    selectedSlot,
    selectedSlotId,
    setSelectedSlotId
  } = useBookingAvailability({
    currentStep,
    availabilityStepNumber: 1,
    eventDate: formData.eventDate,
    eventTime: formData.eventTime,
    searchRole
  });

  const { submitState, handleSubmit } = useBookingSubmission({
    validateCurrentStep,
    booking: bookingForSubmission,
    formData,
    onNavigate,
    servicePage,
    selectedSlot,
    availabilityWindow,
    serviceInfo,
    finalStepNumber
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* TopBar */}
      <TopBar 
        onLocationClick={(location) => console.log('Location clicked:', location)}
        onUserClick={() => onNavigate && onNavigate('login')}
        onCartClick={() => console.log('Cart clicked')}
        onLogoClick={() => onNavigate && onNavigate('home')}
      />

      <BookingBreadcrumb breadcrumb={breadcrumb} onNavigate={onNavigate} />
      <BookingHeader currentStep={currentStep} steps={flow} />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="space-y-8">
                <BookingStepPersonal
                  formData={formData}
                  errors={errors}
                  handleInputChange={handleInputChange}
                />

                <BookingStepEvent
                  formData={formData}
                  errors={errors}
                  handleInputChange={handleInputChange}
                  availabilityWindow={availabilityWindow}
                  availabilityState={availabilityState}
                  searchRole={searchRole}
                  selectedSlotId={selectedSlotId}
                  setSelectedSlotId={setSelectedSlotId}
                />
              </div>

              <BookingFormNavigation
                submitState={submitState}
                totalSteps={totalSteps}
              />
            </form>
          </div>

          <div className="lg:col-span-1">
            <BookingOrderSummary
              serviceInfo={serviceInfo}
              packageInfo={packageInfo}
              addOnItems={addOnItems}
              totalAmount={effectiveTotalAmount}
              pricingBreakdown={effectivePricingBreakdown}
              servicePricingConfig={servicePricingConfig}
              hasDakshinaPricing={hasDakshinaPricing}
              onDakshinaChange={handleDakshinaChange}
              bookingMode={bookingMode}
              selectedPanditName={selectedPanditName}
              formData={formData}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default BookingPage;

